<template>
  <div class="tabs">
    <div
      class="tabs-container"
      :class="{
        'min-width': currentUser && currentUser.id
      }"
    >
      <div class="nav-tabs">
        <div
          v-tooltip="{
            content: 'Presentation',
            trigger: 'hover'
          }"
          id="nav-item-tab-slide"
          data-testid="audience-tabs-presentation-tab"
          class="nav-item nav-item-tab-slide"
          :class="{
            active: activeTabView === 'slide',
          }"
          :open-on-click="false"
          @click="handleSwitchTab(presentation.uniqueAccessCode ? 'presentation' : 'home')"
        >
          <div class="nav-item-background relative">
            <span
              v-if="isConnectionGoodOrUnstable"
              class="connection-indicator"
              :class="{
                'good': isConnectionGood,
                'unstable': isConnectionUnstable,
              }"
            />
          </div>
          <aha-icon
            v-if="isUnleashAndConnectionGoodOrUnstable"
            class="nav-item-icon"
            :name="activeTabView === 'slide' ? 'presenter-solid' : 'presenter-light'"
          />
          <aha-spinner
            variant="dark"
            v-if="isConnectionReconnecting"
          />
        </div>
        <div
          v-if="isEnableSlideReview"
          v-tooltip="{
            content: 'Review',
            trigger: 'hover'
          }"
          id="nav-item-tab-lookback"
          data-testid="audience-tabs-review-tab"
          class="nav-item nav-item-tab-lookback"
          :class="{
            active: activeTabView === 'lookback',
            disabled: isTabReviewDisabled,
          }"
          @click="handleSwitchTab('lookback', isTabReviewDisabled)"
        >
          <div class="nav-item-background" />
          <aha-icon
            class="nav-item-icon"
            name="quiz-answer"
          />
        </div>
        <div
          v-if="isShowTabFeedback"
          v-tooltip="{
            content: 'Feedback',
            trigger: 'hover'
          }"
          id="nav-item-tab-feedback"
          data-testid="audience-tabs-feedback-tab"
          class="nav-item nav-item-tab-feedback"
          :class="{
            active: activeTabView === 'feedback',
            disabled: isTabFeedbackDisabled,
          }"
          @click="handleSwitchTab('feedback', isTabFeedbackDisabled)"
        >
          <div class="nav-item-background" />
          <aha-icon
            class="nav-item-icon"
            :name="activeTabView === 'feedback' ? 'feedback-solid' : 'feedback'"
          />
        </div>
        <div
          v-if="isShowTabProfile"
          v-tooltip="{
            content: 'My Account',
            trigger: 'hover'
          }"
          id="nav-item-tab-profile"
          data-testid="audience-tabs-profile-tab"
          class="nav-item nav-profile nav-item-tab-profile"
          :class="{
            active: activeTabView === 'profile',
          }"
          @click="handleSwitchTab(presentation.uniqueAccessCode ? 'profile' : 'account')"
        >
          <div class="nav-item-background" />
          <aha-icon
            class="nav-item-icon nav-item-icon__profile"
            :name="activeTabView === 'profile' ? 'profile-solid' : 'profile-light'"
          />
        </div>
        <div
          v-if="isZoomApp && isEmbedAudienceApp"
          v-tooltip="{
            content: 'Log out',
            trigger: 'hover'
          }"
          class="nav-item nav-zoom-logout"
          data-testid="audience-tabs-logout-button"
          @click="handleLogoutZoomApp"
        >
          <div class="nav-item-background" />
          <aha-svg
            name="profile_logout"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            :maintainColor="false"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import VTooltip from 'v-tooltip'
import Config from '@/config'
import { CONNECTION_STATUS } from '@/utils/system/SystemConnection'
import { canAccessProfile } from '@/utils/profile-access.utils'
import util from '../util'

VTooltip.enabled = window.innerWidth > 768

export default {
  data() {
    return {
      CONNECTION_STATUS,
    }
  },
  computed: {
    ...mapGetters('zoomApp', ['isZoomApp']),
    ...mapGetters('systemConnection', ['connectionStatus']),
    ...mapGetters(['isEnableSlideReview']),
    ...mapState({
      currentUser: (state) => state.currentUser,
      activeTabView: (state) => state.activeTabView,
      presentation: (state) => state.presentation,
      audienceId: (state) => state.audienceId,
    }),
    isSkoleTube() {
      return this.presentation
        && this.presentation.user
        && this.presentation.user.identifyProvider === 'skoletube'
    },
    isTabReviewDisabled() {
      return !this.audienceId
    },
    isTabFeedbackDisabled() {
      return !this.audienceId
    },
    isShowTabFeedback() {
      return (this.presentation && this.presentation.isEnablePrivateFeedback) || this.isTabFeedbackDisabled
    },
    isAccountTabVisible() {
      return this.presentation && this.presentation.isAccountTabVisible
    },
    isShowTabProfile() {
      return canAccessProfile({
        presentation: this.presentation,
        isSkoleTube: this.isSkoleTube,
        isZoomApp: this.isZoomApp,
      })
    },
    isEmbedAudienceApp() {
      return window.location.href.includes(Config.presenterUrl)
    },

    userId() {
      return this.currentUser && this.currentUser.id
    },
    tabQueryParam() {
      return this.$route.query.tab
    },
    isUnleashAndConnectionGoodOrUnstable() {
      return this.activeTabView !== 'slide' || this.isConnectionGoodOrUnstable
    },
    isConnectionGoodOrUnstable() {
      return this.activeTabView === 'slide' && (this.connectionStatus === CONNECTION_STATUS.GOOD || this.connectionStatus === CONNECTION_STATUS.UNSTABLE)
    },
    isConnectionError() {
      return this.activeTabView === 'slide' && this.connectionStatus === CONNECTION_STATUS.ERROR
    },
    isConnectionReconnecting() {
      return this.activeTabView === 'slide' && this.connectionStatus === CONNECTION_STATUS.RECONNECTING
    },
    isConnectionGood() {
      return this.activeTabView === 'slide' && this.connectionStatus === CONNECTION_STATUS.GOOD
    },
    isConnectionUnstable() {
      return this.activeTabView === 'slide' && this.connectionStatus === CONNECTION_STATUS.UNSTABLE
    },
  },
  watch: {
    '$route.name': {
      handler(value) {
        if (value) {
          const activeTab = this._getActiveTabByRouteName(value)
          this.setActiveTabAction(activeTab)
        }
      },
      immediate: true,
    },
    tabQueryParam: {
      handler(value) {
        if (value) {
          const tab = this._getActiveTabByRouteName(value)

          // Determine the correct route name based on context
          let routeName = tab
          if (tab === 'profile') {
            // If we're on home route (no presentation id), use 'account' route
            // Otherwise use 'profile' route within presentation
            routeName = this.$route.params.id ? 'profile' : 'account'
          }

          this.$router.push({
            name: routeName,
            params: {
              id: this.$route.params.id,
            },
          })
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['setActiveTabAction']),
    trackingSwitchTab(value) {
      const activeTab = this._getActiveTabByRouteName(value)

      if (activeTab === 'profile' && this.currentUser && this.currentUser.id) {
        this.$store.state.lazyTrackingSwitchTab = true

        return
      }

      const mappingTabEventLabel = {
        feedback: 'Feedback',
        profile: 'Account',
        slide: 'Presentation',
        lookback: 'Lookback',
      }

      const trackingProps = {
        eventCategory: 'Audience',
        eventLabel: mappingTabEventLabel[activeTab],
        presentationId: this.presentation && this.presentation.id,
      }

      util.ga4Track('Main screen > Switch tab', trackingProps)
    },
    handleSwitchTab(routeName, isTabDisabled = false) {
      const isTheSameTab = this._getActiveTabByRouteName(routeName) === this.activeTabView

      if (isTabDisabled || isTheSameTab) return

      this.$router.push({
        name: routeName,
        params: {
          id: this.$route.params.id,
        },
      })

      this.trackingSwitchTab(routeName)
    },
    _getActiveTabByRouteName(routeName) {
      const lowercaseRouteName = routeName.toLowerCase()

      switch (lowercaseRouteName) {
        case 'home':
        case 'presentation':
          return 'slide'
        case 'lookback':
          return 'lookback'
        case 'feedback':
          return 'feedback'
        case 'account':
        case 'profile':
          return 'profile'
        default:
          return 'slide'
      }
    },
    handleLogoutZoomApp() {
      window.parent.postMessage('logoutZoomApp', Config.presenterUrl)
    },
  },
}
</script>

<style lang="scss" scoped>
.tabs-container {
  width: 100%;
  background: #eff1f7;
}

.nav-tabs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(78px, 1fr));
  max-width: 710px;
  margin: 0 auto;
  color: #B4BCCF;

  .nav-item {
    padding: 14px;
    flex: auto;
    text-align: center;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #eff1f7;
    color: rgba(0, 0, 0, 0.2);

    &.active {
      background: #fff;
      color: #191F2D;
    }

    &.disabled {
      cursor: not-allowed;
      color: #B4BCCF;
    }
  }
}

.nav-item {
  &:not(.disabled) .nav-item-icon {
    color: #313131;
    fill: none;
  }

  .nav-item-icon__profile {
    color: #eff1f7;
  }
  &.nav-zoom-logout {
    color: black;
  }
}

.nav-item.active {
  .nav-item-background {
    width: 32px;
    height: 32px;
    background: theme('colors.primary.20');
    border-radius: 50%;
    position: absolute;
  }

  .nav-item-icon {
    z-index: 1;
    color: theme('colors.primary.90');
  }

  .nav-item-icon-profile {
    border: 2px solid theme('colors.primary.90');
  }
}

.connection-indicator {
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 100%;
  bottom: -2px;
  right: -2px;
  border: 2px solid #fff;

  &.good {
    background: #33C173;
  }
  &.unstable {
    background: #FFA826;
  }
}
.status-error {
  &::v-deep path[fill="#FF4954"] {
    fill: #FF4954;
  }
}
</style>
